import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ErrorState } from '@/components/ui/States';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';
import { useCompanyRole } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { api, ApiError, type CompanyDetail, type WorkspaceDetail, type ProjectDetail } from '@/lib/api';
import { LayoutHeader, TabBar } from '../company/CompanyLayout';
import { useWorkspaceCtx } from '../workspace/WorkspaceLayout';

export interface ProjectCtx {
  company: CompanyDetail;
  workspace: WorkspaceDetail;
  project: ProjectDetail;
  reloadProject: () => void;
}

export const useProjectCtx = () => useOutletContext<ProjectCtx>();

/** Resolves /c/:companySlug/w/:workspaceSlug/p/:projectSlug — the slug→id hop
 * uses the parent workspace's projects array, already in outlet context. */
export function ProjectLayout() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { company, workspace } = useWorkspaceCtx();
  // Review is visible to staff and company admins (suggestion:view = ADMIN).
  const role = useCompanyRole(company.id);
  const isStaff = role === 'STAFF';

  const project = useAsync(async () => {
    const match = workspace.projects.find((p) => p.slug === projectSlug);
    if (!match) throw new ApiError('Project not found.', 404, 'not_found');
    return api.getProject(match.id);
  }, [workspace.id, projectSlug]);

  if (project.status === 'loading') {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <SkeletonRows rows={3} />
      </div>
    );
  }
  if (project.status === 'error') {
    return <ErrorState error={project.error} onRetry={project.reload} />;
  }

  const p = project.data;
  const base = `/c/${company.slug}/w/${workspace.slug}/p/${p.slug}`;
  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: company.name, to: `/c/${company.slug}` },
          { label: workspace.name, to: `/c/${company.slug}/w/${workspace.slug}` },
          { label: p.name },
        ]}
      />
      <LayoutHeader title={p.name} description={p.goal} />
      <TabBar
        tabs={[
          { to: `${base}/runs`, label: 'Runs' },
          { to: `${base}/deliverables`, label: 'Deliverables' },
          ...(isStaff || role === 'ADMIN' ? [{ to: `${base}/review`, label: 'Review' }] : []),
          ...(isStaff || role === 'ADMIN' ? [{ to: `${base}/knowledge`, label: 'Knowledge' }] : []),
          ...(isStaff ? [{ to: `${base}/settings`, label: 'Settings' }] : []),
        ]}
      />
      <Outlet context={{ company, workspace, project: p, reloadProject: project.reload } satisfies ProjectCtx} />
    </>
  );
}
